import { useEffect, useState } from 'react';
import { Trash2, Star, PenTool, Video } from 'lucide-react';
import PhotoEditor from './PhotoEditor';
import MediaThumb from './MediaThumb';
import { VoiceRecorder, MemoList } from './VoiceMemo';
import { ROLE_KEYS } from '../lib/i18n';
import DictateField from './DictateField';
import { PhotoAiLine } from './PhotoDocBar';
import { getMode } from '../lib/profiles';

function PhotoCard({ t, p, i, onUpdate, onRole, onDelete, onEdit, onMemo, sector }) {
  const isVideo = p.kind === 'video';
  return (
    <div className={`photo-card ${p.role === 'main' ? 'is-main' : ''}`} data-testid={`photo-card-${i}`} style={{ animationDelay: `${i * 40}ms` }}>
      {isVideo ? <MediaThumb item={p} testid={`video-thumb-${i}`} /> : (
        <button className="thumb-btn" data-testid={`photo-thumb-${i}`} onClick={onEdit}>
          <img className="photo-thumb" src={p.data} alt="" />
          {p.annotations?.length > 0 && <span className="edited-tag">{t.edited}</span>}
        </button>
      )}
      <div className="photo-info">
        {p.role === 'main' && <span className="badge-main" data-testid={`photo-main-badge-${i}`}><Star size={11} /> {t.badgeMain}</span>}
        {isVideo ? <span className="badge-video" data-testid={`video-badge-${i}`}><Video size={11} /> {t.video}</span> : (
          <select data-testid={`photo-role-select-${i}`} value={p.role} onChange={(e) => onRole(p.id, e.target.value)}>
            {Object.entries(ROLE_KEYS).filter(([k]) => k !== 'main' || p.role === 'main').map(([k, key]) => <option key={k} value={k}>{t[key]}</option>)}
          </select>
        )}
        <DictateField t={t} testid={`photo-caption-input-${i}`} placeholder={t.captionPlaceholder} value={p.caption || ''} onChange={(v) => onUpdate(p.id, { caption: v })} />
        <MemoList t={t} memos={p.memos} prefix={`photo-${i}`} onDelete={(mid) => onUpdate(p.id, { memos: p.memos.filter((m) => m.id !== mid) })} />
        {!isVideo && getMode() === 'photo' && <PhotoAiLine t={t} p={p} i={i} sector={sector} onUpdate={onUpdate} />}
      </div>
      <div className="photo-actions">
        {!isVideo && (
          <button className="icon-btn" data-testid={`photo-edit-btn-${i}`} onClick={onEdit} aria-label={t.editPhoto} title={t.editPhoto}><PenTool size={16} /></button>
        )}
        <VoiceRecorder t={t} compact testid={`photo-memo-btn-${i}`} onDone={(m) => onMemo(p.id, i, m)} />
        <button className="icon-btn danger" data-testid={`photo-delete-btn-${i}`} onClick={() => onDelete(p.id)} aria-label="delete"><Trash2 size={16} /></button>
      </div>
    </div>
  );
}

export default function PhotoList({ t, photos, onUpdate, onRole, onDelete, onMemo, sector }) {
  const [editId, setEditId] = useState(null);
  useEffect(() => { const h = (e) => setEditId(e.detail); window.addEventListener('giss-photo-edit', h); return () => window.removeEventListener('giss-photo-edit', h); }, []);
  const editing = photos.find((p) => p.id === editId);
  if (!photos.length) return <div className="empty" data-testid="photos-empty">{t.noPhotos}</div>;
  return (
    <div className="photo-list" data-testid="photo-list">
      {editing && (
        <PhotoEditor t={t} photo={editing} onClose={() => setEditId(null)} onSave={(patch) => { onUpdate(editing.id, patch); setEditId(null); }} />
      )}
      {photos.map((p, i) => (
        <PhotoCard key={p.id} t={t} p={p} i={i} sector={sector} onUpdate={onUpdate} onRole={onRole} onDelete={onDelete} onMemo={onMemo} onEdit={() => setEditId(p.id)} />
      ))}
    </div>
  );
}
